import { DataGrid, DataGridProps, OnAction } from '@chipinside/datagrid'
import { Grid } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { omit, sortBy } from 'lodash'

import AnimalDatasheet from '#/components/Datasheets/Animal'
import FormatCell from '#/components/FormatCell'
import {
  checkEditAndDelete,
  rowStyle,
} from '#/pages/Farm/Modules/Herd/Animal/Tabs/ReproductionEvents/manipulate'
import { useAnimal } from '#/store/ducks/context'
import { useFarmSlug } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import { endpoint } from '#/store/services/endpoints'

/** Calf que gera registro de animal (natimorto não gera) */
export const isLiveCalf = calf => calf?.calf_status !== 'stillbirth'

/** Calves do parto que geram registro de animal */
export const getLiveCalves = calving => calving?.parameters?.calves?.filter?.(isLiveCalf) ?? []

export const countLiveCalves = calving => getLiveCalves(calving).length

/**
 * Função responsável por registrar animal após parto
 * Preenche a mãe até que todos os filhos sejam registrados
 */
export const onSuccessActionBirthRegister = ({
  build,
  calves,
  calvings = [] as any[],
  eventSlug,
  farmSlug,
}) => {
  let count = calves ? calves - 1 : undefined

  // para many calving, todos os calves do parto atual já foram registrados — este form pertence ao próximo parto
  if (!calves && calvings?.length > 1) {
    calvings.shift() // remove o parto que já teve todos os calves registrados

    const currentCalving = calvings[0]

    count = countLiveCalves(currentCalving) - 1
    eventSlug = currentCalving?.slug
  }

  // calves do próximo parto mantêm o botão "registrar novo animal" habilitado
  const nextCalving = calvings[1]
  const calvesInNextCalving = countLiveCalves(nextCalving)

  return build({
    formName: 'birth',
    formType: 'register',
    onSuccessAction: () =>
      onSuccessActionBirthRegister({ build, calves: count, calvings, eventSlug, farmSlug }),
    params: { calves: count + calvesInNextCalving, eventSlug, farm: farmSlug },
  })
}

function AnimalReproductionEvents() {
  const animal = useAnimal()
  const { build, confirm } = useFormBuilder()
  const farmSlug = useFarmSlug()
  const permissions = usePermissions()

  const {
    deleted_at: animalDeletedAt,
    earring: animalEarring,
    name: animalName,
    slug: animalSlug,
  } = animal

  const animalStatus = animal?.status?.status
  const lactationStatus = animal?.production_status?.status

  const controller: DataGridProps['controller'] = {
    endpoint: endpoint.farm.animal.reproduction_events_datatable,
    params: { animal: animalSlug, farm: farmSlug },
  }

  function handleOnAction({ key, row }: OnAction) {
    switch (key) {
      case 'reproduction_events_reproductive_evaluation':
        build({
          formName: 'reproduction_events',
          formType: 'reproductive_evaluation_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_heifer_register':
        build({
          formName: 'reproduction_events',
          formType: 'heifer_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_weaning_register':
        build({
          formName: 'reproduction_events',
          formType: 'weaning_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_abortion_register':
        build({
          formName: 'reproduction_events',
          formType: 'abortion_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_insemination_register':
        build({
          formName: 'reproduction_events',
          formType: 'insemination_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_embryo_transfer_register':
        build({
          formName: 'reproduction_events',
          formType: 'embryo_transfer_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_natural_breeding_register':
        build({
          formName: 'reproduction_events',
          formType: 'natural_breeding_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_visual_heat_register':
        build({
          formName: 'reproduction_events',
          formType: 'visual_heat_register',
          onSuccessAction: () =>
            build({
              formName: 'reproduction_events',
              formType: 'insemination_register',
              params: { animalEarring, animalSlug, farm: farmSlug },
            }),
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_pregnancy_diagnosis_register':
        build({
          formName: 'reproduction_events',
          formType: 'pregnancy_diagnosis_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_induced_lactation_register':
        build({
          formName: 'reproduction_events',
          formType: 'induced_lactation_register',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      case 'reproduction_events_drying_register':
        build({
          formName: 'reproduction_events',
          formType: 'drying_register',
          params: {
            farm: farmSlug,
            selectedAnimals: [{ earring: animalEarring, name: animalName, slug: animalSlug }],
          },
        })
        break

      case 'reproduction_events_pre_calving_register':
        build({
          formName: 'reproduction_events',
          formType: 'pre_calving_register',
          params: {
            farm: farmSlug,
            selectedAnimals: [{ earring: animalEarring, name: animalName, slug: animalSlug }],
          },
        })
        break

      case 'reproduction_events_calving_register':
        build({
          formName: 'reproduction_events',
          formType: 'calving_register',
          params: { animalEarring, animalSlug, build, farm: farmSlug },
        })
        break

      case 'update':
        handleUpdateEvent(row)
        break

      case 'delete':
        confirm({
          formName: 'reproduction_events',
          formType: 'delete',
          params: {
            animalSlug,
            eventName: row?.virtual_type_text,
            eventSlug: row.slug,
            farm: farmSlug,
          },
        })
        break

      case 'recovery':
        confirm({
          formName: 'reproduction_events',
          formType: 'recovery',
          params: {
            eventName: row?.virtual_type_text,
            eventSlug: row.slug,
            farm: farmSlug,
          },
        })
        break

      case 'confirm-event':
        {
          const { virtual_type } = row
          if (virtual_type === 'reproduction.birth.algorithm') {
            build({
              formName: 'reproduction_events',
              formType: 'calving_register',
              params: { animalEarring, animalSlug, build, farm: farmSlug },
            })
          } else {
            build({
              formName: 'reproduction_events',
              formType: 'heat_confirm',
              params: {
                animalEarring,
                animalName,
                animalSlug,
                eventSlug: row?.slug,
                farm: farmSlug,
              },
            })
          }
        }
        break

      case 'unconfirm-event':
        confirm({
          formName: 'reproduction_events',
          formType: 'unconfirm',
          params: {
            eventName: row?.virtual_type_text,
            eventSlug: row.slug,
            eventType: row?.virtual_type,
            farm: farmSlug,
          },
        })
        break

      case 'animal_undo_discard':
        build({
          formName: 'animal',
          formType: 'undo_discard',
          params: { animalEarring, animalSlug, farm: farmSlug },
        })
        break

      default: //nothing to moo
    }
  }

  const handleUpdateEvent = row => {
    const { virtual_type } = row
    const updateMap = {
      'birth.birth.any': {
        formName: 'birth',
        formType: 'update',
        params: { animalEarring, animalSlug, eventSlug: row.slug, farm: farmSlug },
      },
      'reproduction.abortion.any': {
        formName: 'reproduction_events',
        formType: 'abortion_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.apt_after_delivery.any': {
        formName: 'reproduction_events',
        formType: 'apt_after_delivery_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.birth.user': {
        formName: 'reproduction_events',
        formType: 'calving_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.dry.any': {
        formName: 'reproduction_events',
        formType: 'drying_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.empty.any': {
        formName: 'reproduction_events',
        formType: 'empty_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.evaluation.any': {
        formName: 'reproduction_events',
        formType: 'reproductive_evaluation_update',
        params: { animalEarring, animalSlug, event: row.slug, farm: farmSlug },
      },
      'reproduction.gynecologic_exam_apt.any': {
        formName: 'reproduction_events',
        formType: 'gynecologic_exam_apt_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.gynecologic_exam_inapt.any': {
        formName: 'reproduction_events',
        formType: 'gynecologic_exam_inapt_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.induced_lactation.any': {
        formName: 'reproduction_events',
        formType: 'induced_lactation_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.pregnant_pre_delivery.any': {
        formName: 'reproduction_events',
        formType: 'pre_calving_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
      'reproduction.visual_heat.any': {
        formName: 'reproduction_events',
        formType: 'visual_heat_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      },
    }

    const embryoTransferTypes = [
      'reproduction.embryo_transfer.normal',
      'reproduction.embryo_transfer.ftai',
      'reproduction.embryo_transfer.ftet',
      'reproduction.embryo_transfer.pg',
      'reproduction.embryo_transfer.iatf',
      'reproduction.embryo_transfer.tetf',
    ]

    const inseminationTypes = [
      'reproduction.inseminated.normal',
      'reproduction.inseminated.ftai',
      'reproduction.inseminated.ftet',
      'reproduction.inseminated.pg',
    ]

    const naturalBreedingTypes = [
      'reproduction.natural_breeding.normal',
      'reproduction.natural_breeding.ftai',
      'reproduction.natural_breeding.ftet',
      'reproduction.natural_breeding.pg',
    ]

    const weaningTypes = ['reproduction.weaning.user', 'reproduction.weaning.algorithm']
    const pregnancyTypes = [
      'reproduction.inconclusive_pregnancy.any',
      'reproduction.negative_pregnancy.any',
      'reproduction.pregnant.any',
    ]
    const heiferTypes = ['reproduction.heifer.user', 'reproduction.heifer.algorithm']

    if (embryoTransferTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'embryo_transfer_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (inseminationTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'insemination_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (naturalBreedingTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'natural_breeding_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (weaningTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'weaning_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (pregnancyTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'pregnancy_diagnosis_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (heiferTypes.includes(virtual_type)) {
      build({
        formName: 'reproduction_events',
        formType: 'heifer_update',
        params: { animalEarring, animalSlug, event: row?.slug, farm: farmSlug },
      })
    } else if (updateMap[virtual_type]) {
      build(updateMap[virtual_type])
    }
  }

  const isMatureCow = status => !['calf', 'heifer'].includes(status)

  const manipulateColumns: DataGridProps['manipulateColumns'] = {
    infos: {
      Cell: ({ row }) => {
        const isCollarHeat = row.virtual_type === 'reproduction.heat.algorithm'
        const isFromAlgorithm = row.creator_type === 'algorithm'

        const mappedRow = {
          ...row,
          ...omit(row?.parameters, ['heat_signs']),
          birth_events: row?.parameters,
          confirmed_as: isCollarHeat ? row.confirmed_as : null,
          confirmed_at: isFromAlgorithm ? row.confirmed_at : null,
        }

        return <FormatCell type="infos" value={mappedRow} />
      },
    },
    status: {
      Cell: ({ row, value }) => <FormatCell type="heat-status" {...row} {...value} />,
    },
  }

  const actionsTop: DataGridProps['actionsTop'] = {
    // Sem ações quando animal está deletado
    items: animalDeletedAt
      ? []
      : sortBy(
          [
            {
              enabled: () => permissions.farm.create_reproduction_event && animalStatus === 'empty',
              key: 'reproduction_events_reproductive_evaluation',
              title: Translate({ messageKey: 'reproductive_evaluation' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && animalStatus?.includes('calf'),
              key: 'reproduction_events_weaning_register',
              title: Translate({ messageKey: 'weaning' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && animalStatus === 'heifer',
              key: 'reproduction_events_heifer_register',
              title: Translate({ messageKey: 'release_to_reproduction' }),
            },
            {
              enabled: () => permissions.farm.create_reproduction_event && animalStatus !== 'calf',
              key: 'reproduction_events_insemination_register',
              title: Translate({ messageKey: 'insemination' }),
            },
            {
              enabled: () => permissions.farm.create_reproduction_event && animalStatus !== 'calf',
              key: 'reproduction_events_embryo_transfer_register',
              title: Translate({ messageKey: 'embryo_transfer' }),
            },
            {
              enabled: () => permissions.farm.create_reproduction_event && animalStatus !== 'calf',
              key: 'reproduction_events_natural_breeding_register',
              title: Translate({ messageKey: 'natural_mating' }),
            },
            {
              enabled: () => permissions.farm.create_reproduction_event && animalStatus !== 'calf',
              key: 'reproduction_events_visual_heat_register',
              title: Translate({ messageKey: 'visual_heat' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event &&
                !['heifer', 'calf', 'calf-new-born', 'empty-post-delivery'].includes(animalStatus),
              key: 'reproduction_events_pregnancy_diagnosis_register',
              title: Translate({ messageKey: 'pregnancy_diagnosis' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event &&
                ['empty', 'inseminated', 'late-inseminated'].includes(animalStatus),
              key: 'reproduction_events_induced_lactation_register',
              title: Translate({ messageKey: 'induced_lactation' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && lactationStatus === 'lactating',
              key: 'reproduction_events_drying_register',
              title: Translate({ messageKey: 'drying' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && ['pregnant'].includes(animalStatus),
              key: 'reproduction_events_pre_calving_register',
              title: Translate({ messageKey: 'pre_calving' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && isMatureCow(animalStatus),
              key: 'reproduction_events_calving_register',
              title: Translate({ messageKey: 'calving' }),
            },
            {
              enabled: () =>
                permissions.farm.create_reproduction_event && animalStatus?.includes('pregnant'),
              key: 'reproduction_events_abortion_register',
              title: Translate({ messageKey: 'abortion' }),
            },
          ],
          'title',
        ),
    onAction: handleOnAction,
    title: Translate({ messageKey: 'register_item' }),
  }

  const actionsColumn: DataGridProps['actionsColumn'] = [
    {
      items: [
        {
          enabled: row =>
            !animalDeletedAt &&
            permissions.farm.update_reproduction_event &&
            checkEditAndDelete(row, 'update') &&
            row.virtual_type !== 'birth.birth.any',
          key: 'update',
          title: Translate({ messageKey: 'update_item' }),
        },
        {
          enabled: row =>
            !animalDeletedAt &&
            permissions.farm.update_birth_event &&
            row.virtual_type === 'birth.birth.any',
          key: 'update',
          title: Translate({ messageKey: 'update_item' }),
        },
        {
          enabled: row =>
            !animalDeletedAt && permissions.farm.update_reproduction_event && !!row.deleted_at,
          key: 'recovery',
          title: Translate({ messageKey: 'recover_this_register' }),
        },
        {
          enabled: row =>
            !animalDeletedAt &&
            permissions.farm.confirm_reproduction_event &&
            !row.deleted_at &&
            !row.confirmed_at &&
            !row.confirmed_as &&
            row.virtual_type !== 'birth.birth.any',
          key: 'confirm-event',
          title: Translate({ messageKey: 'confirm_item' }),
        },
        {
          enabled: row =>
            !animalDeletedAt &&
            permissions.farm.confirm_reproduction_event &&
            row.virtual_type?.match(/reproduction.(heat|birth).algorithm/) &&
            !!row.confirmed_at,
          key: 'unconfirm-event',
          title: Translate({
            messageKey: 'revert_item',
            params: { item: Translate({ lower: true, messageKey: 'confirmation' }) },
          }),
        },
        {
          enabled: row =>
            !animalDeletedAt &&
            permissions.farm.delete_reproduction_event &&
            checkEditAndDelete(row, 'delete') &&
            row.virtual_type !== 'birth.birth.any',
          key: 'delete',
          level: 'error',
          title: Translate({ messageKey: 'remove_item' }),
        },
        {
          enabled: row =>
            animalDeletedAt &&
            permissions.farm.delete_discard_event &&
            row?.virtual_type?.match(/discard.[^.]+?.(user|any)/),
          key: 'animal_undo_discard',
          title: Translate({ messageKey: 'undo_discart' }),
        },
      ],
      onAction: handleOnAction,
      type: 'dropdown',
    },
  ]

  return (
    <Grid key="animal-reproduction-container" container spacing={1} sx={{ mt: 2 }}>
      <Grid key="animal-reproduction-datagrid" size={{ md: 9, sm: 12 }}>
        <DataGrid
          controller={controller}
          manipulateColumns={manipulateColumns}
          actionsTop={actionsTop}
          actionsColumn={actionsColumn}
          rowStyle={rowStyle}
          withInfos
        />
      </Grid>
      <Grid key="animal-reproduction-datasheet" size={{ md: 3, sm: 12 }}>
        <AnimalDatasheet />
      </Grid>
    </Grid>
  )
}

export default AnimalReproductionEvents
